import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/Layout";
import {
  APIProvider,
  Map,
  Marker,
  InfoWindow,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { getLocationByTask } from "../../api/locationApi";
import { useLocation, useNavigate } from "react-router-dom";
import { Polyline } from "../../components/Polyline";
import { IoMdArrowBack } from "react-icons/io";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const MapContent = ({ locations, path, selected, setSelected }) => {
  const geocodingLib = useMapsLibrary("geocoding"); // ✅ must be inside APIProvider
  const [address, setAddress] = useState("N/A");

  useEffect(() => {
    // ✅ Both must be ready — geocodingLib AND selected
    if (!geocodingLib || !selected) return;

    const geocoder = new geocodingLib.Geocoder();
    setAddress("N/A");

    geocoder
      .geocode({
        location: { lat: selected.latitude, lng: selected.longitude },
      })
      .then((res) => {
        if (res.results[0]) {
          setAddress(res.results[0].formatted_address);
        } else {
          setAddress("N/A");
        }
      })
      .catch((err) => {
        console.error("Geocode error:", err);
        setAddress("N/A");
      });
  }, [geocodingLib, selected]); // ✅ correct deps — NOT geocoder state

  return (
    <Map
      style={{ width: "100%", height: "600px" }}
      defaultCenter={{
        lat: locations[0]?.latitude,
        lng: locations[0]?.longitude,
      }}
      defaultZoom={12}
    >
      <Polyline
        path={path}
        strokeColor="#0000FF"
        strokeOpacity={1.0}
        strokeWeight={3}
      />

      {locations.map((loc) => (
        <Marker
          key={loc.id}
          position={{ lat: loc.latitude, lng: loc.longitude }}
          onClick={() => setSelected(loc)}
        />
      ))}

      {selected && (
        <InfoWindow
          position={{ lat: selected.latitude, lng: selected.longitude }}
          onCloseClick={() => setSelected(null)}
        >
          <div
            style={{
              fontFamily: "'Segoe UI', sans-serif",
              minWidth: "200px",
              maxWidth: "250px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <strong style={{ fontSize: "14px", color: "#1a1a1a" }}>
                👤 {selected.userName}
              </strong>
              <span
                style={{
                  background:
                    selected.eventType === "start" ? "#d4edda" : "#f8d7da",
                  color: selected.eventType === "start" ? "#155724" : "#721c24",
                  borderRadius: "12px",
                  padding: "2px 10px",
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {selected.eventType}
              </span>
            </div>

            <hr style={{ margin: "6px 0", borderColor: "#f0f0f0" }} />

            {[
              {
                icon: "🗓️",
                label: new Date(selected.timestamp).toLocaleDateString(),
              },
              {
                icon: "⏰",
                label: new Date(selected.timestamp).toLocaleTimeString(),
              },
              { icon: "📍", label: address },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "flex-start",
                  marginBottom: "5px",
                  fontSize: "12px",
                  color: "#555",
                }}
              >
                <span>{item.icon}</span>
                <span style={{ lineHeight: "1.4", maxWidth: "200px" }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </InfoWindow>
      )}
    </Map>
  );
};

const Location = () => {
  const { state } = useLocation();
  const taskLogid = state?.taskLogid;
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [locations, setLocations] = useState([]);
  const [path, setPath] = useState([]);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    getLocationCordinate();
  }, []);

  const getLocationCordinate = async () => {
    await getLocationByTask(taskLogid).then((res) => {
      if (res.status === 200) {
        if (res.data.data.locationLogs.length > 0) {
          let arrayOfLocation = [];
          let arrayofPath = [];
          res.data.data.locationLogs.map((loc, index) => {
            arrayOfLocation.push({
              id: index,
              latitude: loc.latitude,
              longitude: loc.longitude,
              userName: state?.employee,
              eventType: loc.events,
              timestamp: loc.createdAt,
            });
            arrayofPath.push({ lat: loc.latitude, lng: loc.longitude });
          });
          setLocations(arrayOfLocation);
          setPath(arrayofPath);
        } else {
          alert("No Employee Location found for this task");
        }
      }
    });
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <div
          className="d-flex align-items-center cursor-pointer"
          onClick={() => navigate("/employeetasks")}
        >
          <IoMdArrowBack size={24} color={"black"} />
          <span
            style={{
              paddingLeft: 10,
              fontSize: "22px",
              fontWeight: 600,
              minWidth: "140px",
            }}
          >
            {state?.employee} Location History
          </span>
        </div>
        <span style={{ fontSize: "14px", color: "#6c757d" }}>
          Task Date: {new Date(locations[0]?.timestamp).toLocaleDateString()}{" "}
          {new Date(locations[0]?.timestamp).toLocaleTimeString()}
        </span>
      </div>
      <APIProvider apiKey={API_KEY}>
        {locations.length > 0 && (
          // ✅ MapContent is inside APIProvider so useMapsLibrary works
          <MapContent
            locations={locations}
            path={path}
            selected={selected}
            setSelected={setSelected}
          />
        )}
      </APIProvider>
    </Layout>
  );
};

export default Location;
